import type { SessionArtwork } from '@modelcast/types';

interface PinnedArtworkOverlayProps {
  artwork: SessionArtwork;
  canClose?: boolean;
  onClose: () => void;
  onLike: (artworkId: string) => void;
  participantId: string;
  roomCode: string;
}

export function PinnedArtworkOverlay({
  artwork,
  canClose = true,
  onClose,
  onLike,
  participantId,
  roomCode,
}: PinnedArtworkOverlayProps) {
  const handleLike = async () => {
    try {
      const response = await fetch(`/api/rooms/${roomCode}/artwork/${artwork.id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ participantId }),
      });

      if (response.ok) {
        onLike(artwork.id);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  return (
    <div className="absolute inset-0 bg-[#0a0807] flex items-center justify-center z-20">
      {/* Artwork image */}
      <img
        src={artwork.imageUrl}
        alt={`Artwork by ${artwork.participantName}`}
        className="max-w-full max-h-full object-contain"
      />

      {/* Top bar with spotlight controls */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-start justify-between bg-gradient-to-b from-black/60 to-transparent">
        {/* Artist info */}
        <div className="flex items-center gap-3">
          <div>
            <div className="text-white font-medium text-sm">
              {artwork.artistProfile?.displayName || artwork.participantName}
            </div>
            {artwork.medium && (
              <div className="text-white/60 text-xs">
                {artwork.medium}
              </div>
            )}
          </div>
        </div>

        {canClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            aria-label="Clear artwork spotlight"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom bar with like button */}
      <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between bg-gradient-to-t from-black/60 to-transparent">
        {/* Model credit if available */}
        <div className="text-white/80 text-xs">
          {(artwork.modelName || artwork.modelProfile) && (
            <span>
              Model:{' '}
              {artwork.modelProfile ? (
                <a
                  href={`/profile/${artwork.modelProfile.id}`}
                  className="text-accent hover:underline"
                >
                  {artwork.modelName || artwork.modelProfile.displayName}
                </a>
              ) : (
                artwork.modelName
              )}
            </span>
          )}
        </div>

        {/* Like button */}
        <button
          onClick={handleLike}
          className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border transition-colors ${
            artwork.isLikedByMe
              ? 'bg-white text-[#151515] border-white hover:bg-[#f8f4ea]'
              : 'bg-black/50 text-white border-white/20 hover:bg-black/70'
          }`}
        >
          <svg
            className="w-5 h-5"
            viewBox="0 0 24 24"
            fill={artwork.isLikedByMe ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span className="font-medium">{artwork.likesCount || 0}</span>
        </button>
      </div>
    </div>
  );
}
